import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import {
  Upload,
  Button,
  Message,
  Row,
  Col,
  Space,
  Popover,
  Tooltip,
} from '@bedrock/components';
import { Upload as UploadIcon, Eye, ChevronDown } from '@bedrock/icons-react';
import {
  UploadChangeParam,
  UploadFile,
  UploadProps,
} from '@bedrock/components/lib/Upload/types';
import classNames from 'classnames';
import './Supload.less';
import { $sType, localPx2rem } from '#utils';
import {
  attachmentBlobDownloadUrl,
  outsourceAttachmentDownloadUrl,
} from '#services/api';
import { WithViewBaseConfigProvider } from '#features/PxToRemWrapper';
import { validate } from 'uuid';
import { adaptMpcExternalOuterPath } from '#utils/axios';

export type AttachmentDots = {
  id: number;
  blob?: {
    filename: string;
    byte_size: number;
    id: string;
    key: string;
    content_type: string;
  };
  uid?: string;
  status?: 'error' | 'success' | 'done' | 'uploading' | 'removed' | 'pending';
} & UploadFile;

/** 图片附件判定（导出供调用方复用，避免双写漂移） */
export const IMAGE_FILE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
/** PDF 附件：始终走浏览器新页签预览 */
const PDF_FILE_RE = /\.pdf$/i;
/** 未传入 onPreviewFile 时的既有预览入口白名单（向后兼容，勿改） */
const LEGACY_PREVIEW_RE = /(png|jpe?g|pdf|html|xlsx|xls)$/i;

type FileUploaderProps = {
  fileList?: AttachmentDots[];
  limit?: number;
  view?: boolean;
  onChange?: (
    info: {
      dataList: AttachmentDots[];
    } & UploadChangeParam
  ) => void;
  dataParams?: {
    model: string;
    attach_type: string;
  };
  downLoadPath?: string | ((fileId: string | number) => string);
  action?: string;
  /**
   * 可选：接管附件预览入口（属性值扩展）。
   *
   * - **不传（默认）**：完全沿用既有行为 —— 命中 `png|jpe?g|pdf|html|xlsx|xls`
   *   的附件展示预览图标，点击后 `window.open` 新页签预览。
   * - **传入**：预览入口收敛为「图片 + PDF」——
   *   图片回调给调用方自行预览（如应用内 ImageViewer），
   *   PDF 仍走 `window.open` 新页签，其余格式不展示预览入口（仅可下载）。
   *
   * 新规则仅在显式传入该属性的调用方生效，其余调用方零影响。
   * 命名刻意避开 bedrock `UploadProps.onPreview`：同名会拦截其原生预览语义，
   * 导致非图片/PDF 的预览图标静默消失。
   */
  onPreviewFile?: (file: AttachmentDots, fileList: UploadFile[]) => void;
} & Omit<UploadProps, 'fileList' | 'onChange' | 'action'>;

type WrapperProps = {
  ellipsis?: boolean;
  ellipsisNum?: number;
  inline?: boolean;
  /** 查看且仅有一个文件时，是否直接展示文件 */
  viewOneShow?: boolean;
  popoverProps?: any;
  suffix?: React.ReactNode;
} & FileUploaderProps;

const SUpload = forwardRef((props: FileUploaderProps, ref) => {
  const defaultPathMap = useMemo(
    () => ({
      inner: {
        action: '/api/v2/java_proxy/attachments/upload',
        downLoadPath: '/api/v2/java_proxy/attachments/',
      },
      outer: {
        action: adaptMpcExternalOuterPath(
          '/api/outsource/v2/java_proxy/attachments/upload'
        ),
        downLoadPath: adaptMpcExternalOuterPath(
          '/api/outsource/v2/java_proxy/attachments/'
        ),
      },
    }),
    [adaptMpcExternalOuterPath]
  );

  const defaultPath = defaultPathMap[$sType];
  const {
    className,
    limit,
    fileList = [],
    view = false,
    onChange,
    title = '上传文件',
    fileSizeLimit = 100 * 1024 * 1024,
    tip = '单个文件不能超过100M',
    action = defaultPath.action,
    downLoadPath = defaultPath.downLoadPath,
    dataParams = {
      model: 'V2::DemandSupplier',
      attach_type: 'demand_evaluation_attachs',
    },
    name,
    suffix,
    onPreviewFile,
    ...rest
  } = props;

  const uploadRef = useRef(null);
  const localFileList = useRef<any[]>(fileList);

  useImperativeHandle(ref, () => {
    return {
      validateFields: () => {
        // 校验文件,如果文件有上传中，或者上传失败，则校验失败
        if (localFileList.current.some((n) => n.status === 'uploading')) {
          return Promise.reject('有文件正在上传中，请稍等');
        }
        if (localFileList.current.some((n) => n.status === 'error')) {
          return Promise.reject('有文件上传失败，请删除');
        }
        return Promise.resolve();
      },
      ...uploadRef?.current,
    };
  });

  const showList = useMemo(() => {
    return fileList.map((n = {} as AttachmentDots) => {
      const { status, blob, id, uid, ...rets } = n;
      const showId = blob?.id || id;
      let url = `${downLoadPath}${showId}?download=attachment&isBlob=true`;
      // 根据id是否相同判断是否是blob下载地址
      // TODO: 测试没暂时注释
      // if (id && +id !== +blob?.id) {
      //   url = `${downLoadPath}${id}?download=attachment`
      // }
      if (typeof downLoadPath === 'function') {
        url = downLoadPath(showId);
      }
      return {
        id: showId,
        uid: uid || id,
        name: blob?.filename || '',
        url,
        size: blob?.byte_size || 0,
        status: status || 'done',
        key: blob?.key,
        blob,
        ...rets,
      } as UploadFile;
    });
  }, [fileList]);

  const handleDownloadUrl = async (file, download) => {
    const showId = file?.blob?.id || file.id;
    let result;
    if ($sType === 'inner') {
      result = await attachmentBlobDownloadUrl({
        id: showId,
        is_blob: true,
        download,
      });
    } else {
      result = await outsourceAttachmentDownloadUrl({
        id: showId,
        is_blob: true,
        download,
      });
    }
    result?.data && window.open(result?.data);
  };

  const renderIcon = (file: UploadFile) => {
    const fileName = file.name || '';

    // 属性扩展：调用方传入 onPreviewFile 时启用新规则
    // （图片 → 回调给调用方应用内预览；PDF → 新页签；其他 → 无预览入口，仅下载）
    if (onPreviewFile) {
      if (IMAGE_FILE_RE.test(fileName)) {
        return (
          <Button
            type="text-subtle"
            size="xSmall"
            icon={<Eye />}
            onClick={() => {
              // renderIcon 收到的 file 即 showList 的元素（由本组件构造，运行时必含 id/blob），
              // 但 bedrock 的 renderIcon 签名只声明 UploadFile，此处按真实结构收窄
              onPreviewFile(file as unknown as AttachmentDots, showList);
            }}
          />
        );
      }
      if (PDF_FILE_RE.test(fileName)) {
        return (
          <Button
            type="text-subtle"
            size="xSmall"
            icon={<Eye />}
            onClick={() => {
              handleDownloadUrl(file, 'preview');
            }}
          />
        );
      }
      return null;
    }

    // 缺省路径：既有行为保持不变
    if (!LEGACY_PREVIEW_RE.test(fileName)) {
      return null;
    }
    return (
      <Button
        type="text-subtle"
        size="xSmall"
        icon={<Eye />}
        onClick={() => {
          handleDownloadUrl(file, 'preview');
        }}
      />
    );
  };

  const handleDownload = async (file: UploadFile) => {
    handleDownloadUrl(file, 'attachment');
  };

  return (
    <WithViewBaseConfigProvider>
      {
        <div
          className={classNames(
            'render-file-list-or-upload-button-container sform-upload',
            className
          )}
        >
          <Upload
            ref={uploadRef}
            style={{
              width: '100%',
            }}
            className={classNames({
              'file-list-readonly': view,
              className,
            })}
            title={title}
            fileList={showList}
            fileSizeLimit={fileSizeLimit}
            renderIcon={renderIcon}
            limit={limit}
            onDownload={handleDownload}
            onChange={(e) => {
              onChange?.({
                dataList:
                  e?.fileList?.map?.((n: any) => {
                    const id =
                      n?.blob?.id || n?.response?.data?.blob_id || n?.id;
                    const responseData = n?.response?.data as {
                      blob_id: number;
                      blob_key: string;
                      filename: string;
                      size: number;
                    };
                    // 上传报错时，改变状态
                    // bedrock原逻辑只有在http的外层code不等于200时
                    let responseState = n.status || 'done';
                    if (n?.response && n?.response?.code !== 1) {
                      responseState = 'error';
                    }
                    return {
                      id: id,
                      status: responseState,
                      blob: {
                        filename: n?.name,
                        byte_size: n?.size || responseData?.size,
                        id: id,
                        key: n?.key || responseData?.blob_key,
                        content_type: n?.originFileObj?.type,
                      },
                      uid: n.uid || id,
                    };
                  }) || [],
                ...e,
              });
              localFileList.current = e?.fileList || [];
            }}
            tip={tip}
            trigger={
              <Button
                size={'medium'}
                type="checked"
                icon={
                  <UploadIcon
                    translate={undefined}
                    onAuxClick={undefined}
                    onAuxClickCapture={undefined}
                  />
                }
              >
                上传文件
              </Button>
            }
            onError={(err, file) => {
              if (err.message === 'Exceed Limit') {
                Message.error(`文件限制最多上传${limit}个`);
              } else if (err.message === 'Size Limit' && file) {
                Message.error(`文件${file.name}大小超出限制`);
              } else if (err.message === 'Network Error') {
                Message.error(`网络连接断开`);
              }
            }}
            data={(file: { name: string }) => {
              return {
                filename: file?.name || '',
                ...dataParams,
              };
            }}
            name={'file'}
            action={action}
            {...rest}
            {...(props.style
              ? {
                  style: {
                    ...props.style,
                    width: localPx2rem(props.style?.width),
                  },
                }
              : {})}
            multiple
          />
        </div>
      }
      {suffix}
    </WithViewBaseConfigProvider>
  );
});

const SUploadWrapper = forwardRef((props: WrapperProps, ref) => {
  const {
    ellipsis = false,
    ellipsisNum = 6,
    suffix,
    inline,
      viewOneShow = true,
    popoverProps,
    ...rest
  } = props;
  const [expand, setExpand] = useState(false);
  const innerRef = useRef<any>(null);

  const [localFileList, setLocalFileList] = useState<any[]>(
    props.fileList || []
  );

  useEffect(() => {
    if (props.fileList) {
      setLocalFileList(props.fileList);
    }
  }, [props.fileList]);

  const isUploading = useMemo(() => {
    return localFileList.some((n) => n.status === 'uploading');
  }, [localFileList]);

  useImperativeHandle(ref, () => innerRef.current);

  const showFileList = useMemo(() => {
    if (!ellipsis) {
      return props.fileList;
    }
    return expand ? props.fileList : props.fileList?.slice(0, ellipsisNum);
  }, [props.fileList, ellipsisNum, ellipsis, expand]);

  if (inline) {
    const isSingleView = props.view && props.fileList?.length === 1;
    if (isSingleView && viewOneShow) {
      return (
        <SUpload
          {...rest}
          ref={innerRef}
          fileList={props.fileList}
          className="upload-list-mode"
          tip={null}
        />
      );
    }
    return (
      <div
        className={classNames('sform-upload-inline-wrapper', props.className)}
      >
        <Space className="upload-container-inner" size={8}>
          {!props.view && (
            <SUpload
              {...rest}
              ref={innerRef}
              className="upload-button-mode"
              fileList={props.fileList}
              onChange={(file) => {
                setLocalFileList(file.fileList || []);
                props.onChange?.(file);
              }}
              trigger={
                <Tooltip title={props.tip || '上传附件'} placement="topRight">
                  <Button
                    type="checked-neutral"
                    icon={<UploadIcon />}
                    loading={isUploading}
                  />
                </Tooltip>
              }
              tip={null}
            />
          )}
          {!!props.fileList?.length &&
            (isSingleView && viewOneShow ? (
              <SUpload
                {...rest}
                ref={innerRef}
                fileList={props.fileList}
                className="upload-list-mode"
                tip={null}
              />
            ) : (
              <>
                {!!props.fileList?.length && (
                  <Popover
                    content={
                      <div className="sform-upload-popover-content">
                        <SUpload
                          {...rest}
                          className="upload-list-mode"
                          fileList={props.fileList}
                          multiple
                          onChange={(file) => {
                            setLocalFileList(file.fileList || []);
                            props.onChange?.(file);
                          }}
                          tip={null}
                        />
                      </div>
                    }
                    // trigger="click"
                    disabled={!props.fileList?.length}
                    {...popoverProps}
                  >
                    <Button type="link" suffixIcon={<ChevronDown />}>
                      {props.view
                        ? `${props.fileList?.length || 0}个附件`
                        : `共${props.fileList?.length || 0}个`}
                    </Button>
                  </Popover>
                )}
                {!props.fileList?.length && props.view && (
                  <span className="file-empty-tip">无附件</span>
                )}
              </>
            ))}
          {suffix}
        </Space>
      </div>
    );
  }

  if (ellipsis && props.view) {
    return (
      <>
        <Row gutter={[8, 8]}>
          {showFileList?.map((item, index) => {
            return (
              <Col span={8} key={index}>
                <SUpload {...rest} fileList={[item]} />
              </Col>
            );
          })}
        </Row>
        {ellipsis &&
          !!props.fileList?.length &&
          props.fileList.length > ellipsisNum && (
            <Button
              type="link"
              onClick={() => setExpand(!expand)}
              className="m-t-4"
            >
              {expand
                ? '收起'
                : `展开剩余 ${props.fileList.length - ellipsisNum} 项`}
            </Button>
          )}
        {suffix}
      </>
    );
  }

  return <SUpload ref={innerRef} {...rest} />;
});

export { SUploadWrapper as SUpload };
